import { inject, Injectable } from '@angular/core';
import { PasswordSessionService } from './password-session.service';
import { ApiService } from './api.service';
import { TokenStorageService } from './token-storage.service';
import { SingleResponse } from '@/app/shared/types';
import { defer, finalize, shareReplay, tap } from 'rxjs';

import {
    LoginRequestDto,
    LoginResponseDto,
    RefreshTokenRequestDto,
    RefreshTokenResponseDto,
    PasswordRemindRequestDto,
    PasswordResetRequestDto,
    ChallengeBaseResponseDto,
} from '@qc/shared/dto';

@Injectable({ providedIn: 'root' })
export class AuthService extends ApiService {
    private readonly tokenStorage = inject(TokenStorageService);
    private readonly passwordSessions = inject(PasswordSessionService);
    private refreshRequest: SingleResponse<RefreshTokenResponseDto> | null = null;
    protected get basePath(): string {
        return '/auth';
    }

    login(credentials: LoginRequestDto) {
        return this.post<LoginResponseDto>('login', credentials);
    }

    completeLogin() {
        return this.post<LoginResponseDto>('login/complete');
    }

    logout() {
        return this.postVoid('logout');
    }

    refreshToken(refreshToken: string) {
        // Activity renewal, 401 retries and auth effects must share one rotation.
        if (!this.refreshRequest) {
            const currentToken = this.tokenStorage.getRefreshToken() ?? refreshToken;
            const request: RefreshTokenRequestDto = { refreshToken: currentToken };
            this.refreshRequest = defer(() => this.post<RefreshTokenResponseDto>('refresh', request)).pipe(
                tap(({ data }) => {
                    if (this.tokenStorage.getRefreshToken() !== currentToken) {
                        throw new Error('Session changed during token refresh');
                    }
                    if (!data.accessToken || !data.refreshToken) throw new Error('Invalid token response');
                    this.tokenStorage.saveTokens(data.accessToken, data.refreshToken);
                }),
                finalize(() => { this.refreshRequest = null; }),
                shareReplay({ bufferSize: 1, refCount: false }),
            );
        }
        return this.refreshRequest;
    }

    forgotPassword(request: PasswordRemindRequestDto) {
        return this.postVoid('password/request', request);
    }

    resetPassword(request: PasswordResetRequestDto) {
        return this.post<ChallengeBaseResponseDto>('password/reset', request);
    }

    changePassword({
        currentPassword,
        newPassword,
        confirmPassword,
    }: {
        currentPassword: string;
        newPassword: string;
        confirmPassword: string;
    }) {
        const identity = this.passwordSessions.identity(this.tokenStorage.getAccessToken());
        return this.post<LoginResponseDto>('password/change', { currentPassword, newPassword, confirmPassword }).pipe(
            tap(() => this.passwordSessions.passwordChanged(identity)),
        );
    }
}
